import React, { Fragment } from 'react';
import { GoGift, GoNote, GoPackage } from 'react-icons/go';
import { isNull } from 'lodash';
import { setDash } from '../common';
import { setDateFormat } from '../moment';
import InputForm from '../../components/form/InputForm';
import { formRequiredRules } from '../formRules';
import DateRangePicker from '../../components/datePicker/DateRangePicker';
import Img from '../../components/Img/Img';
import couponProductArrowImg from '../../static/images/coupon/couponDetailPreview/ic-product-arrow.png';
import couponBrandArrowImg from '../../static/images/coupon/couponDetailPreview/ic-brand-arrow.png';
import couponCheckImg from '../../static/images/coupon/couponDetailPreview/ic-coupon-check.png';
import { DATE_FORMAT } from '../../constants/service/common/utilConstants';
import {
  COUPON_AVAILABLE_TYPE,
  COUPON_DAY_PLUS_MAXIMUM_DAY,
  COUPON_DAY_PLUS_MINIMUM_DAY,
  COUPON_DISCOUNT_PRICE_AMOUNT_MAXIMUM_PRICE,
  COUPON_DISCOUNT_PRICE_AMOUNT_MINIMUM_PRICE,
  COUPON_DISCOUNT_RATE_AMOUNT_MAXIMUM_PRICE,
  COUPON_DISCOUNT_RATE_AMOUNT_MINIMUM_PRICE,
  COUPON_DISCOUNT_TYPE,
  COUPON_DISCOUNT_TYPE_TEXT,
  COUPON_GENERATE_LIMIT_COUNT_MAX,
  COUPON_GENERATE_LIMIT_COUNT_MIN,
  COUPON_GENERATE_LIMIT_DAY_MAX,
  COUPON_GENERATE_LIMIT_DAY_MIN,
  COUPON_GENERATE_LIMIT_TYPE,
  COUPON_GENERATE_LIMIT_TYPE_TEXT,
  COUPON_MAX_DISCOUNT_MAXIMUM_PRICE,
  COUPON_MAX_DISCOUNT_MINIMUM_PRICE,
  COUPON_MINIMUM_PURCHASE_MAXIMUM_PRICE,
  COUPON_MINIMUM_PURCHASE_MINIMUM_PRICE,
  COUPON_PERIOD_TYPE,
  PLACEHOLDER_TEXT_COUPON_PERIOD_DATE,
} from '../../constants/service/app/couponConstants';
import { REQUIRED_MSG } from '../../constants/service/common/formRulesConstants';
import formatNumberWithComma from './common/formatComma';
/**
 *  단위 입력 검증 함수
 */
export const isUnitValidate = (unit, message) => {
  return (_, value) => (value % unit === 0 ? Promise.resolve() : Promise.reject(message));
};

/**
 * 브랜드 분담률 validation - 쿠폰 할인율을 넘을 수 없음
 */
export const validateBrandDiscountRate = (discountAmount) => {
  return {
    validator: (_, value) => {
      if (value > discountAmount) {
        return Promise.reject(`브랜드 분담률은 쿠폰 할인율(${discountAmount}%)을 넘을 수 없습니다.`);
      }
      return Promise.resolve();
    },
  };
};

/**
 * 브랜드 분담액 validation - 쿠폰 할인액을 넘을 수 없음
 */
export const validateBrandDiscountAmount = (discountAmount) => {
  return {
    validator: (_, value) => {
      if (value > discountAmount) {
        return Promise.reject(`브랜드 분담액은 쿠폰 할인액(${discountAmount?.toLocaleString()}원)을 넘을 수 없습니다.`);
      }
      return Promise.resolve();
    },
  };
};

/**
 * 쿠폰 할인율 validation - 브랜드 분담률보다 작을 수 없음
 */
export const validateCouponDiscountRate = (settlementShare) => {
  return {
    validator: (_, value) => {
      if (settlementShare && settlementShare.shareType === 'rate' && settlementShare.shareAmount > 0) {
        if (value < settlementShare.shareAmount) {
          return Promise.reject(`쿠폰 할인율은 브랜드 분담률(${settlementShare.shareAmount}%)보다 작을 수 없습니다.`);
        }
      }
      return Promise.resolve();
    },
  };
};

/**
 * 쿠폰 할인액 validation - 브랜드 분담액보다 작을 수 없음
 */
export const validateCouponDiscountAmount = (settlementShare) => {
  return {
    validator: (_, value) => {
      if (settlementShare && settlementShare.shareType === 'amount' && settlementShare.shareAmount > 0) {
        if (value < settlementShare.shareAmount) {
          return Promise.reject(
            `쿠폰 할인액은 브랜드 분담액(${settlementShare.shareAmount?.toLocaleString()}원)보다 작을 수 없습니다.`,
          );
        }
      }
      return Promise.resolve();
    },
  };
};

/**
 *  쿠폰 유효 기간
 */
export const setCouponPeriod = (type, period, dayPlus) => {
  /**
   *  시작 종료 날짜 지정 기준
   */
  if (type === COUPON_PERIOD_TYPE.START_AND_END_DATE) {
    return `${setDateFormat(period.startDate, DATE_FORMAT.FULL_DASH_FORMAT)} ~ ${setDateFormat(
      period.endDate,
      DATE_FORMAT.FULL_DASH_FORMAT,
    )}`;
  } else if (type === COUPON_PERIOD_TYPE.DAY_PLUS) {
    /**
     *   발급일 +날짜 기준
     */
    return `발급일로부터 ${dayPlus}일 동안 사용이 가능합니다.`;
  } else if (type === COUPON_PERIOD_TYPE.INFINITE) {
    /**
     *  무기한
     */
    return '무기한';
  }
};

/**
 *  쿠폰 제한 타입
 */
export const setCouponGenerateLimitType = (generateLimitDay) => {
  /**
   *  generateLimitDay 가 1 이상 일때는 주기당 몇번 이다.
   */
  if (generateLimitDay >= 1) {
    return COUPON_GENERATE_LIMIT_TYPE.MULTIPLE_CYCLE;
  } else if (generateLimitDay === 0) {
    /**
     *  generateLimitDay 가 0 일때는 평생에 몇번 이다.
     */
    return COUPON_GENERATE_LIMIT_TYPE.MULTIPLE_FOREVER;
  } else if (generateLimitDay === null) {
    /**
     *  generateLimitDay 가 null 일때는 무제한이다.
     */
    return COUPON_GENERATE_LIMIT_TYPE.UNLIMIT;
  }
};

/**
 *  쿠폰 발급 제한 횟수
 */
export const setCouponGenerateLimitCount = (generateLimitDay, generateLimitCount) => {
  /**
   *  generateLimitDay 가 null 일때는 무제한이다.
   */
  if (isNull(generateLimitDay)) {
    return COUPON_GENERATE_LIMIT_TYPE_TEXT[COUPON_GENERATE_LIMIT_TYPE.UNLIMIT];
  } else if (generateLimitDay >= 1) {
    /**
     *  generateLimitDay 가 1 이상 일때는 주기당 몇번 이다.
     */
    return `${generateLimitDay}일 안에 ${generateLimitCount}번`;
  } else if (generateLimitDay === 0) {
    /**
     *  generateLimitDay 가 0 일때는 평생에 몇번 이다.
     */
    return `${generateLimitCount}번`;
  } else {
    return '-';
  }
};

/**
 *  쿠폰 혜택
 */
export const setDiscountType = (discountType, discountAmount) => {
  if (discountType === COUPON_DISCOUNT_TYPE.RATE) {
    return COUPON_DISCOUNT_TYPE_TEXT[discountType] + ' ' + formatNumberWithComma(discountAmount) + '%';
  } else {
    return COUPON_DISCOUNT_TYPE_TEXT[discountType] + ' ' + formatNumberWithComma(discountAmount) + '원';
  }
};

/**
 *  할인 혜택
 */
export const setDiscountBenefit = (discountType, discountAmount, minimumPurchasePrice, maxDiscountPrice) => {
  if (discountType === COUPON_DISCOUNT_TYPE.RATE) {
    return (
      `${formatNumberWithComma(minimumPurchasePrice)}원 이상 구매시 ` +
      `${formatNumberWithComma(discountAmount)}% 할인 쿠폰 ` +
      `최대 ${formatNumberWithComma(maxDiscountPrice)}원 `
    );
  } else if (discountType === COUPON_DISCOUNT_TYPE.PRICE) {
    return (
      `${formatNumberWithComma(minimumPurchasePrice)}원 이상 구매시 ` +
      `${formatNumberWithComma(discountAmount)}원 할인 쿠폰 `
    );
  }
};

/**
 *  리스트에 쿠폰 적용 가격, 쿠폰 적용 할인액 속성 넣기
 */
export const setApplyCouponDiscount = (targetList, discountType, discountAmount) => {
  /**
   *  쿠폰적용가격, 쿠폰적용할인액 컬럼 적용
   */
  let couponApplyPrice = 0;
  let couponApplyDiscountAmount = 0;

  targetList = targetList.map((item) => {
    let couponApplySalesPrice = null;
    /**
     *  정액 할인일때
     */
    if (discountType === COUPON_DISCOUNT_TYPE.PRICE) {
      /**
       *  상품 할인이 유효하던 유효하지 않던,
       *  쿠폰 적용 가는 실 판매가에서 쿠폰 할인 액을 뺀 금액이다.
       */
      couponApplyPrice = item.salesPrice - discountAmount;
      /**
       *  originPrice, salesPrice 가 다르다는 것은
       *  product 에 적용이 되는 할인이 유효하다는 뜻이다.
       */
      if (item.originPrice !== item.salesPrice) {
        /**
         *  product 할인이 유효하므로, 쿠폰 적용 할인액은 product 할인액 + 쿠폰 할인액 이다.
         */
        couponApplySalesPrice = discountAmount + item.discountPrice;
      } else {
        /**
         *  product 할인이 유효하지 않으므로, 쿠폰 적용 할인액은 쿠폰 할인액 이다.
         */
        couponApplySalesPrice = discountAmount;
      }
    } else if (discountType === COUPON_DISCOUNT_TYPE.RATE) {
      /**
       *  정률 할인인 경우에는 할인 액을 계산해야된다.
       */
      /**
       *  상품 할인이 유효하던 유효하지 않던,
       *  쿠폰 적용 가는 실 판매가에서 쿠폰 할인율을 뺀 금액이다.
       */
      couponApplyPrice = item.salesPrice - item.salesPrice * (discountAmount / 100);
      /**
       *  originPrice, salesPrice 가 다르다는 것은
       *  product 에 적용이 되는 할인이 유효하다는 뜻이다.
       */
      if (item.originPrice !== item.salesPrice) {
        /**
         *  product 할인이 유효하므로, 쿠폰 적용 할인액은 product 할인액 + 쿠폰 할인율 이다.
         */
        couponApplySalesPrice = item.salesPrice * (discountAmount / 100) + item.discountPrice;
      } else {
        /**
         *  product 할인이 유효하지 않으므로, 쿠폰 적용 할인액은 쿠폰 할인율 이다.
         */
        couponApplySalesPrice = item.salesPrice * (discountAmount / 100);
      }
    }

    couponApplyDiscountAmount = `${typeof couponApplySalesPrice === 'number' ? formatNumberWithComma(couponApplySalesPrice) : '-'}(${Math.floor(
      (couponApplySalesPrice / item.originPrice) * 100,
    )}%)`;

    return {
      ...item,
      couponApplyPrice,
      couponApplyDiscountAmount,
    };
  });

  return targetList;
};

/**
 *  선택된 상품 리스트에서 조건이 유효하지 않는 row 체크하기
 */
export const getCouponProductValidationClassName = (discountType, salesPrice, discountAmount, maxDiscountPrice) => {
  if (discountType === COUPON_DISCOUNT_TYPE.RATE && salesPrice * (discountAmount / 100) > maxDiscountPrice) {
    /**
     *  정률일때 쿠폰 할인액이 쿠폰 최대 할인액을 넘어설 수 없다.
     */
    return 'is-invalid';
  }
};

/**
 *  쿠폰 적용 가능 범위 타입에 따른 아이콘
 */
export const setAvailableTypeBtn = (type) => {
  /**
   *  전체 상품 쿠폰
   */
  if (type === COUPON_AVAILABLE_TYPE.TOTAL_PRODUCT_COUPON) return <GoGift />;
  /**
   *  특정 상품 쿠폰
   */ else if (type === COUPON_AVAILABLE_TYPE.TARGET_PRODUCT_COUPON) return <GoPackage />;
  /**
   *  브랜드 쿠폰
   */ else if (type === COUPON_AVAILABLE_TYPE.TARGET_BRAND_COUPON) return <GoNote />;
};

/**
 *  쿠폰 유형에 따른 couponPreview 아이콘
 */
export const setCouponPreviewBtn = (type) => {
  /**
   *  특정 상품 쿠폰
   */
  if (type === COUPON_AVAILABLE_TYPE.TARGET_PRODUCT_COUPON) {
    return (
      <div className="product-link">
        <div className="product-link-text">적용상품 보기</div>
        <Img clickAble={false} src={couponProductArrowImg} />
      </div>
    );
  } else if (type === COUPON_AVAILABLE_TYPE.TARGET_BRAND_COUPON) {
    /**
     *  특정 브랜드 쿠폰
     */
    return (
      <div className="brand-link">
        <div className="brand-link-text">적용브랜드 보기</div>
        <Img clickAble={false} src={couponBrandArrowImg} />
      </div>
    );
  } else {
    /**
     *  그냥 쿠폰
     */
    return (
      <div className="all-product-link">
        <div>전상품 적용가능</div>
        <Img clickAble={false} src={couponCheckImg} />
      </div>
    );
  }
};

/**
 *  쿠폰 할인 타입에 따른 탭 컨텐츠(수정 가능)
 */
export const setDiscountTypeContentForm = (form, setData, isEditable, data) => {
  const { type, discountType, minimumPurchasePrice, discountAmount, maxDiscountPrice, settlementShare } = data;

  // 선택된 할인 타입과 탭 리스트의 타입이 다를 경우 렌더링 방지
  if (discountType !== type) return null;

  /**
   *  정액 할인
   */
  if (type === COUPON_DISCOUNT_TYPE.PRICE) {
    const isRequired = discountType === COUPON_DISCOUNT_TYPE.PRICE;
    return (
      <Fragment>
        <div className="info">정액 할인 쿠폰은 특정 금액 이상 구매 시, 입력한 (원)만큼 할인이 적용되는 쿠폰입니다.</div>
        <div className="setting">
          {/* 최소 구매 금액 */}
          <InputForm
            type="number"
            form={form}
            style={{ width: 120 }}
            rules={[formRequiredRules(isRequired, REQUIRED_MSG, false), isUnitValidate(10, '10원 단위로 입력해주세요')]}
            min={COUPON_MINIMUM_PURCHASE_MINIMUM_PRICE}
            max={COUPON_MINIMUM_PURCHASE_MAXIMUM_PRICE}
            step={10}
            initialValue={minimumPurchasePrice}
            onChange={(value) =>
              setData({
                name: 'minimumPurchasePrice',
                value,
              })
            }
            addonAfter="원"
            isEditable={isEditable}
            name="minimumPurchasePrice"
          />

          <span className="text">이상 구매 시</span>

          {/*쿠폰 혜택 수치 금액*/}
          <InputForm
            type="number"
            form={form}
            style={{ width: 120 }}
            rules={[
              formRequiredRules(isRequired, REQUIRED_MSG, false),
              isUnitValidate(10, '10원 단위로 입력해주세요'),
              validateCouponDiscountAmount(settlementShare),
            ]}
            min={COUPON_DISCOUNT_PRICE_AMOUNT_MINIMUM_PRICE}
            max={COUPON_DISCOUNT_PRICE_AMOUNT_MAXIMUM_PRICE}
            step={10}
            initialValue={discountAmount}
            onChange={(value) => {
              setData({
                name: 'discountAmount',
                value,
              });
              // 브랜드 분담 validation 재검증
              setTimeout(() => {
                form.validateFields(['settlementShareAmount']);
              }, 0);
            }}
            addonAfter="원"
            isEditable={isEditable}
            name="discountAmount"
          />

          <span className="text">할인</span>
        </div>
      </Fragment>
    );
  } else if (type === COUPON_DISCOUNT_TYPE.RATE) {
    /**
     *  정률 할인
     */
    const isRequired = discountType === COUPON_DISCOUNT_TYPE.RATE;
    return (
      <Fragment>
        <div className="info">
          정률 할인 쿠폰은 특정 금액 이상 구매 시, 입력한 (%)만큼 할인이 적용되는 쿠폰입니다.
          <br />
          정률 쿠폰은 쿠폰 적용 할인액이 최대 할인 가능 금액을 넘어서는 경우 사용이 불가능합니다
        </div>
        <div className="setting">
          {/* 최소 구매 금액 */}
          <InputForm
            type="number"
            form={form}
            style={{ width: 120 }}
            rules={[formRequiredRules(isRequired, REQUIRED_MSG, false), isUnitValidate(10, '10원 단위로 입력해주세요')]}
            min={COUPON_MINIMUM_PURCHASE_MINIMUM_PRICE}
            max={COUPON_MINIMUM_PURCHASE_MAXIMUM_PRICE}
            step={10}
            initialValue={minimumPurchasePrice}
            onChange={(value) =>
              setData({
                name: ['minimumPurchasePrice'],
                value,
              })
            }
            addonAfter="원"
            isEditable={isEditable}
            name="minimumPurchasePrice"
          />

          <span className="text">이상 구매 시</span>

          {/*쿠폰 혜택 수치 금액*/}
          <InputForm
            type="number"
            form={form}
            style={{ width: 120 }}
            rules={[formRequiredRules(isRequired, REQUIRED_MSG, false), validateCouponDiscountRate(settlementShare)]}
            min={COUPON_DISCOUNT_RATE_AMOUNT_MINIMUM_PRICE}
            max={COUPON_DISCOUNT_RATE_AMOUNT_MAXIMUM_PRICE}
            step={1}
            initialValue={discountAmount}
            onChange={(value) => {
              setData({
                name: 'discountAmount',
                value,
              });
              // 브랜드 분담 validation 재검증
              setTimeout(() => {
                form.validateFields(['settlementShareAmount']);
              }, 0);
            }}
            addonAfter="%"
            unit="%"
            isEditable={isEditable}
            name="discountAmount"
          />

          <span className="text">를 할인하고, </span>

          {/* 최대 할인 가능 금액 */}
          <InputForm
            type="number"
            form={form}
            style={{ width: 120 }}
            rules={[
              formRequiredRules(isRequired, REQUIRED_MSG, false),
              isUnitValidate(1000, '1,000원 단위로 입력해주세요'),
            ]}
            min={COUPON_MAX_DISCOUNT_MINIMUM_PRICE}
            max={COUPON_MAX_DISCOUNT_MAXIMUM_PRICE}
            step={1000}
            initialValue={maxDiscountPrice}
            onChange={(value) =>
              setData({
                name: 'maxDiscountPrice',
                value,
              })
            }
            addonAfter="원"
            isEditable={isEditable}
            name="maxDiscountPrice"
          />

          <span className="text">까지 할인이 가능합니다.</span>
        </div>
      </Fragment>
    );
  }
};

/**
 *  쿠폰 할인 타입에 따른 탭 컨텐츠(수정 불가능)
 */
export const setDiscountTypeContent = (data) => {
  const { type, minimumPurchasePrice, discountAmount, maxDiscountPrice } = data;

  /**
   *  정액 할인
   */
  if (type === COUPON_DISCOUNT_TYPE.PRICE) {
    return (
      <Fragment>
        <div className="info">정액 할인 쿠폰은 특정 금액 이상 구매 시, 입력한 (원)만큼 할인이 적용되는 쿠폰입니다.</div>
        <div className="setting">
          {/* 최소 구매 금액 */}
          <span className="price">{minimumPurchasePrice ? formatNumberWithComma(minimumPurchasePrice) : '-'}원</span>

          <span className="text">이상 구매 시</span>

          {/*쿠폰 혜택 수치 금액*/}
          <span className="price">{discountAmount ? formatNumberWithComma(discountAmount) : '-'}원</span>

          <span className="text">할인</span>
        </div>
      </Fragment>
    );
  } else if (type === COUPON_DISCOUNT_TYPE.RATE) {
    /**
     *  정률 할인
     */
    return (
      <Fragment>
        <div className="info">정률 할인 쿠폰은 특정 금액 이상 구매 시, 입력한 (%)만큼 할인이 적용되는 쿠폰입니다.</div>
        <div className="setting">
          {/* 최소 구매 금액 */}
          <span className="price">{minimumPurchasePrice ? formatNumberWithComma(minimumPurchasePrice) : '-'}원</span>

          <span className="text">이상 구매 시</span>

          {/*쿠폰 혜택 수치 금액*/}
          <span className="price">{discountAmount ? formatNumberWithComma(discountAmount) : '-'}%</span>

          <span className="text">를 할인하고, </span>

          {/* 최대 할인 가능 금액 */}
          <span className="price">{maxDiscountPrice ? formatNumberWithComma(maxDiscountPrice) : '-'}원</span>

          <span className="text">까지 할인이 가능합니다.</span>
        </div>
      </Fragment>
    );
  }
};

/**
 *  쿠폰 기간 타입에 따른 탭 컨텐츠
 */
export const setPeriodTypeContent = (form, setData, isEditable, data) => {
  const { type, periodType, period, dayPlus } = data;
  /**
   *  날짜 지정
   */
  if (type === COUPON_PERIOD_TYPE.START_AND_END_DATE) {
    return (
      <Fragment>
        <div className="info">날짜 지정 쿠폰은, 관리자가 해당 쿠폰을 생성할 때 지정한 기간에만 사용이 가능합니다.</div>
        <div className="setting">
          <DateRangePicker
            value={period}
            showTime={true}
            outFormat={DATE_FORMAT.FULL_DASH_FORMAT}
            isEditable={isEditable}
            startText={PLACEHOLDER_TEXT_COUPON_PERIOD_DATE}
            endText={PLACEHOLDER_TEXT_COUPON_PERIOD_DATE}
            btnTextFormat={DATE_FORMAT.FULL_DASH_FORMAT}
            format={DATE_FORMAT.FULL_DASH_FORMAT}
            onSelect={(data) =>
              setData({
                path: ['period'],
                value: data,
              })
            }
          />
        </div>
      </Fragment>
    );
  } else if (type === COUPON_PERIOD_TYPE.DAY_PLUS) {
    /**
     *  발급일 기준
     */
    return (
      <Fragment>
        <div className="info">
          발급일 기준 쿠폰은, 발급일 기준으로 사용가능한 날짜를 지정한 기간까지만 사용이 가능합니다.
        </div>
        <div className="setting">
          <span className="text">발급일로부터</span>

          {/* 최소 구매 금액 */}
          <InputForm
            type="number"
            form={form}
            style={{ width: 120 }}
            rules={[formRequiredRules(periodType === COUPON_PERIOD_TYPE.DAY_PLUS, REQUIRED_MSG, false)]}
            min={COUPON_DAY_PLUS_MINIMUM_DAY}
            max={COUPON_DAY_PLUS_MAXIMUM_DAY}
            step={1}
            initialValue={dayPlus}
            onChange={(value) =>
              setData({
                path: ['dayPlus'],
                value,
              })
            }
            addonAfter="일"
            unit="일"
            isEditable={isEditable}
            name="dayPlus"
          />

          <span className="text"> 동안 사용이 가능합니다.</span>
        </div>
      </Fragment>
    );
  } else if (type === COUPON_PERIOD_TYPE.INFINITE) {
    /**
     *  무기한
     */
    return (
      <Fragment>
        <div className="info">
          무기한 사용가능 쿠폰은, 무기한 사용이 가능한 쿠폰이니 <span className="emphasize">주의</span>해서 발급합니다.
        </div>
      </Fragment>
    );
  }
};

/**
 *  쿠폰 발급 제한 횟수 타입에 따른 탭 컨텐츠
 */
export const setGenerateLimitTypeContent = (form, setData, isEditable, data) => {
  const { type, generateLimitType, generateLimitCount, generateLimitDay } = data;
  /**
   *  주기당 몇번
   */
  if (type === COUPON_GENERATE_LIMIT_TYPE.MULTIPLE_CYCLE) {
    return (
      <Fragment>
        <div className="info">주기당 몇번 쿠폰은 주기에 맞춰 발급이 가능합니다.</div>
        <div className="setting">
          {/*쿠폰 발급 제한 기한 날짜*/}
          <InputForm
            type="number"
            form={form}
            style={{ width: 120 }}
            rules={[
              formRequiredRules(generateLimitType === COUPON_GENERATE_LIMIT_TYPE.MULTIPLE_CYCLE, REQUIRED_MSG, false),
            ]}
            min={COUPON_GENERATE_LIMIT_DAY_MIN}
            max={COUPON_GENERATE_LIMIT_DAY_MAX}
            step={1}
            initialValue={generateLimitDay}
            onChange={(value) =>
              setData({
                path: ['generateLimitDay'],
                value,
              })
            }
            addonAfter="일"
            unit="일"
            isEditable={isEditable}
            name="generateLimitDay"
          />

          <span className="text">안에</span>

          {/*쿠폰 발급 제한 기한 횟수*/}
          <InputForm
            type="number"
            form={form}
            style={{ width: 120 }}
            rules={[
              formRequiredRules(generateLimitType === COUPON_GENERATE_LIMIT_TYPE.MULTIPLE_CYCLE, REQUIRED_MSG, false),
            ]}
            min={COUPON_GENERATE_LIMIT_COUNT_MIN}
            max={COUPON_GENERATE_LIMIT_COUNT_MAX}
            step={1}
            initialValue={generateLimitCount}
            onChange={(value) =>
              setData({
                path: ['generateLimitCount'],
                value,
              })
            }
            addonAfter="번"
            unit="번"
            isEditable={isEditable}
            name="generateLimitCount"
          />

          <span className="text">발급 가능합니다.</span>
        </div>
      </Fragment>
    );
  } else if (type === COUPON_GENERATE_LIMIT_TYPE.MULTIPLE_FOREVER) {
    /**
     *  평생에 몇번
     */
    return (
      <Fragment>
        <div className="info">평생에 몇번 쿠폰은 평생동안 여러번 발급이 가능합니다.</div>
        <div className="setting">
          <span className="text">평생동안 </span>

          {/*쿠폰 발급 제한 기한 횟수*/}
          <InputForm
            type="number"
            form={form}
            style={{ width: 120 }}
            rules={[
              formRequiredRules(generateLimitType === COUPON_GENERATE_LIMIT_TYPE.MULTIPLE_FOREVER, REQUIRED_MSG, false),
            ]}
            min={COUPON_GENERATE_LIMIT_COUNT_MIN}
            max={COUPON_GENERATE_LIMIT_COUNT_MAX}
            step={1}
            initialValue={generateLimitCount}
            onChange={(value) =>
              setData({
                path: ['generateLimitCount'],
                value,
              })
            }
            addonAfter="번"
            unit="번"
            isEditable={isEditable}
            name="generateLimitCount"
          />

          <span className="text">발급 가능합니다.</span>
        </div>
      </Fragment>
    );
  } else if (type === COUPON_GENERATE_LIMIT_TYPE.UNLIMIT) {
    /**
     *  무제한
     */
    return (
      <Fragment>
        <div className="info">평생 동안 무한정 발급 가능합니다.</div>
      </Fragment>
    );
  }
};

export const getCouponPeriodText = ({ type, startDate, endDate, dayPlus }) => {
  if (type === COUPON_PERIOD_TYPE.START_AND_END_DATE) {
    return `${setDateFormat(startDate, DATE_FORMAT.FULL_DASH_FORMAT)} ~ ${setDateFormat(
      endDate,
      DATE_FORMAT.FULL_DASH_FORMAT,
    )}`;
  }

  if (type === COUPON_PERIOD_TYPE.DAY_PLUS) {
    return `발급일 기준으로 ${setDash(dayPlus)}일`;
  }

  if (type === COUPON_PERIOD_TYPE.INFINITE) {
    return '무기한';
  }

  return null;
};
